// LuviaRx — Admin confirmation modal for voiding / refunding a MANUAL order.
// Manual orders (cash / Venmo / Zelle / comp) don't run through Stripe so
// there's no automatic refund path — this UI + POST /admin/orders/{n}/void
// marks the order Refunded, records the reason, and either restocks the
// items (default) or permanently decrements stock when the return is
// damaged / non-restockable.
import React, { useState } from "react";
import axios from "axios";
import { X as XIcon, Loader2, Ban } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function VoidManualOrderModal({ token, order, onClose, onVoided }) {
  const [reason, setReason] = useState("");
  const [restock, setRestock] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  if (!order) return null;

  const submit = async () => {
    setError("");
    setSubmitting(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/orders/${order.order_number}/void`,
        { reason: reason.trim() || null, restock },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      onVoided?.(data);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Failed to void order.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="void-manual-order-modal"
      className="fixed inset-0 z-[60] flex items-center justify-center bg-charcoal-800/60 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md bg-cream-100 rounded-3xl shadow-2xl border border-charcoal-100"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between p-6 border-b border-charcoal-100">
          <div>
            <p className="overline text-rose-700">Void / Refund</p>
            <h2 className="font-display text-xl text-charcoal-800 mt-1">
              Refund order {order.order_number}?
            </h2>
            <p className="text-sm text-charcoal-500 mt-1">
              Marks the order Refunded, keeps it in history, and (by default)
              restocks the items. Use this for cash / Venmo / Zelle refunds
              you&apos;ve handled off-platform.
            </p>
          </div>
          <button
            type="button"
            data-testid="void-manual-close"
            onClick={onClose}
            className="p-2 rounded-full hover:bg-charcoal-100 text-charcoal-500 shrink-0"
            aria-label="Close"
          >
            <XIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div className="text-sm bg-white rounded-2xl border border-charcoal-100 p-3 flex justify-between">
            <span className="text-charcoal-500">Amount to refund</span>
            <span className="font-medium text-charcoal-800">
              ${Number(order.total || 0).toFixed(2)}
            </span>
          </div>

          <div>
            <label className="overline block mb-2">
              Reason <span className="text-charcoal-400 normal-case tracking-normal text-[11px]">(optional)</span>
            </label>
            <textarea
              data-testid="void-manual-reason"
              className="input min-h-[80px]"
              placeholder="e.g. Customer requested cancellation, damaged in transit…"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={500}
            />
          </div>

          <label
            className="flex items-start gap-2 text-sm text-charcoal-700 cursor-pointer bg-white rounded-2xl border border-charcoal-100 p-3"
          >
            <input
              type="checkbox"
              data-testid="void-manual-restock"
              className="mt-0.5"
              checked={restock}
              onChange={(e) => setRestock(e.target.checked)}
            />
            <span>
              <span className="font-medium">Restock items</span>
              <span className="block text-charcoal-500 text-[13px] mt-0.5">
                Uncheck if the units are damaged, opened, or otherwise
                unsellable — stock will be permanently deducted.
              </span>
            </span>
          </label>

          {error && (
            <div
              data-testid="void-manual-error"
              className="p-3 rounded-2xl bg-red-50 border border-red-200 text-red-700 text-sm"
            >
              {error}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 p-5 border-t border-charcoal-100 bg-cream-200/40 rounded-b-3xl">
          <button
            type="button"
            data-testid="void-manual-cancel"
            onClick={onClose}
            className="px-4 py-2 rounded-full text-sm border border-charcoal-200 hover:bg-cream-200"
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="void-manual-submit"
            onClick={submit}
            disabled={submitting}
            className="px-5 py-2 rounded-full text-sm font-medium bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-50 inline-flex items-center gap-2"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Ban className="w-4 h-4" />}
            {submitting ? "Voiding…" : "Confirm refund"}
          </button>
        </div>
      </div>
    </div>
  );
}
